import merge from "lodash/merge";

// Inlined (not extended from `@chakra-ui/react/*`): those subpaths aren't resolvable here.
export const drawerSlotRecipe = merge(
  {
    className: "chakra-drawer",
    slots: [
      "trigger",
      "backdrop",
      "positioner",
      "content",
      "header",
      "body",
      "footer",
      "title",
      "description",
      "closeTrigger"
    ],
    base: {
      backdrop: {
        bg: "blackAlpha.500",
        pos: "fixed",
        insetInlineStart: 0,
        top: 0,
        w: "100vw",
        h: "100dvh",
        "--drawer-z-index": "zIndex.popover",
        zIndex: "calc(var(--drawer-z-index) + var(--layer-index, 0) - 1)",
        _open: {
          animationName: "fade-in",
          animationDuration: "slow"
        },
        _closed: {
          animationName: "fade-out",
          animationDuration: "moderate"
        }
      },
      positioner: {
        display: "flex",
        width: "100vw",
        height: "100dvh",
        position: "fixed",
        insetInlineStart: 0,
        top: 0,
        "--drawer-z-index": "zIndex.popover",
        zIndex: "calc(var(--drawer-z-index) + var(--layer-index, 0))",
        overscrollBehaviorY: "none"
      },
      content: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        width: "100%",
        outline: 0,
        "--drawer-z-index": "zIndex.popover",
        zIndex: "calc(var(--drawer-z-index) + var(--layer-index, 0))",
        textStyle: "sm",
        maxH: "100dvh",
        color: "inherit",
        boxShadow: "lg",
        _open: {
          animationDuration: "slowest",
          animationTimingFunction: "ease-in-smooth"
        },
        _closed: {
          animationDuration: "slower",
          animationTimingFunction: "ease-in-smooth"
        }
      },
      header: {
        display: "flex",
        alignItems: "center",
        gap: "2",
        flex: 0,
        px: "6",
        pt: "6",
        pb: "4"
      },
      body: {
        px: "6",
        py: "2",
        flex: "1",
        overflow: "auto"
      },
      footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "3",
        px: "6",
        pt: "2",
        pb: "4"
      },
      title: {
        flex: "1",
        textStyle: "lg",
        fontWeight: "semibold"
      },
      description: {
        color: "fg.muted"
      },
      closeTrigger: {
        pos: "absolute",
        top: "3",
        insetEnd: "2"
      }
    },
    variants: {
      size: {
        xs: { content: { maxW: "xs" } },
        sm: { content: { maxW: "md" } },
        md: { content: { maxW: "lg" } },
        lg: { content: { maxW: "2xl" } },
        xl: { content: { maxW: "4xl" } },
        full: { content: { maxW: "100vw", h: "100dvh" } }
      },
      placement: {
        start: {
          positioner: {
            justifyContent: "flex-start",
            alignItems: "stretch"
          },
          content: {
            _open: {
              animationName: {
                base: "slide-from-left-full, fade-in",
                _rtl: "slide-from-right-full, fade-in"
              }
            },
            _closed: {
              animationName: {
                base: "slide-to-left-full, fade-out",
                _rtl: "slide-to-right-full, fade-out"
              }
            }
          }
        },
        end: {
          positioner: {
            justifyContent: "flex-end",
            alignItems: "stretch"
          },
          content: {
            _open: {
              animationName: {
                base: "slide-from-right-full, fade-in",
                _rtl: "slide-from-left-full, fade-in"
              }
            },
            _closed: {
              animationName: {
                base: "slide-to-right-full, fade-out",
                _rtl: "slide-to-left-full, fade-out"
              }
            }
          }
        },
        top: {
          positioner: {
            justifyContent: "stretch",
            alignItems: "flex-start"
          },
          content: {
            maxW: "100%",
            _open: { animationName: "slide-from-top-full, fade-in" },
            _closed: { animationName: "slide-to-top-full, fade-out" }
          }
        },
        bottom: {
          positioner: {
            justifyContent: "stretch",
            alignItems: "flex-end"
          },
          content: {
            maxW: "100%",
            _open: { animationName: "slide-from-bottom-full, fade-in" },
            _closed: { animationName: "slide-to-bottom-full, fade-out" }
          }
        }
      },
      contained: {
        true: {
          positioner: { padding: "4" },
          content: { borderRadius: "l3" }
        }
      }
    },
    defaultVariants: {
      size: "xs",
      placement: "end"
    }
  },
  {
    base: {
      backdrop: {
        bg: "blackAlpha.600",
        "--drawer-z-index": "zIndex.modal",
        zIndex: "calc(var(--drawer-z-index) + var(--layer-index, 0) - 1)"
      },
      positioner: {
        "--drawer-z-index": "zIndex.modal",
        zIndex: "calc(var(--drawer-z-index) + var(--layer-index, 0))"
      },
      content: {
        "--drawer-z-index": "zIndex.modal",
        zIndex: "calc(var(--drawer-z-index) + var(--layer-index, 0))",
        _dark: { bg: "gray.700", boxShadow: "dark-lg" }
      },
      header: {
        px: "6",
        pt: "4",
        pb: "4",
        fontSize: "xl",
        fontWeight: "semibold"
      },
      body: {
        px: "6",
        py: "2",
        flex: "1",
        overflow: "auto"
      },
      footer: {
        px: "6",
        pt: "4",
        pb: "4"
      },
      closeTrigger: {
        top: "2",
        insetEnd: "3"
      }
    },
    variants: {
      variant: {
        primary: {
          content: {
            bg: "gray.100",
            boxShadow: "none",
            maxW: { base: "100%", lg: "50%", xl: "33.33%", "2xl": "25%" }
          }
        },
        secondary: {
          content: {
            bg: "gray.200"
          }
        }
      }
    }
  }
);
